/**
 * src/shell/save/unread.ts — 事件未读标记（评审对齐项 3：外壳本地状态）。
 *
 * 引擎事件流没有 read 概念；外壳记录"每段事件流我读到了第几 seq"，
 * 驱动桌面电话灯与部门角标（设计稿 §4.3）。不落引擎事件流，不进导出。
 */

import { LASTREAD_KEY } from './keys'
import { replayEvents } from '../../gm'

type LastReadMap = Record<string, number> // 事件段标识（projectId 或 '_global'）→ 已读 seq

function readMap(): LastReadMap {
  try {
    const raw = localStorage.getItem(LASTREAD_KEY)
    return raw ? (JSON.parse(raw) as LastReadMap) : {}
  } catch {
    return {}
  }
}

export function lastReadSeq(segment: string): number {
  return readMap()[segment] ?? 0
}

export function markRead(segment: string, seq: number): void {
  const map = readMap()
  if ((map[segment] ?? 0) >= seq) return
  map[segment] = seq
  localStorage.setItem(LASTREAD_KEY, JSON.stringify(map))
}

/** 是否有未读（currentMaxSeq 由调用方从 replayEvents 取得） */
export function hasUnread(segment: string, currentMaxSeq: number): boolean {
  return currentMaxSeq > lastReadSeq(segment)
}

/**
 * 接电话即已读（2026-08-15 拍板，缺陷 A+B 修复）：拨号盘 mount = 听完所有留言。
 * 全局段（结算封盘摘要、系统播报）+ 在研项目段（最新一个 status≠'completed'，
 * 取法与 CockpitScreen 一致）一并标记。调用时机只在 mount——之后新到的事件
 * 不算已读，红灯会再亮（这正是设计）。子屏行为不变（项目部现有 markRead 保留，幂等）。
 */
export function markAllSegmentsRead(
  projects: ReadonlyArray<{ id: string; status: string; created_at: string }>,
): void {
  const global = replayEvents(null)
  if (global.length > 0) markRead('_global', global[global.length - 1].seq)
  const active = projects
    .filter((p) => p.status !== 'completed')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
  if (!active) return
  const events = replayEvents(active.id)
  if (events.length > 0) markRead(active.id, events[events.length - 1].seq)
}
